import { AloftPrice, SalesComparisonApproachType } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectCompSequence,
  selectCompTypes,
  selectSalesComparisonApproach,
} from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectSubjectUnitRateForFeature } from '../propertyDataSelectors/unitRateSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { calculateUnitRateAdjustment } from './utils/calculateUnitRateAdjustment.ts';

const calculateActualAgeAdj = (
  improvements: ReturnType<typeof selectImprovementsEntities>,
  compID: string,
  compTypes: ReturnType<typeof selectCompTypes>,
  salesComparisonApproach: SalesComparisonApproachType,
  unitRate?: AloftPrice,
) => {
  const compType = compTypes[compID];
  let subjectImprovements = improvements.subject;
  if (compType === 'arv') subjectImprovements = improvements.subjectArv;

  const compImprovements = improvements[compID];
  if (!subjectImprovements || !compImprovements) return undefined;

  const { age } = subjectImprovements;
  const { age: compAge } = compImprovements;

  if (salesComparisonApproach === 'unit_rate') {
    return calculateUnitRateAdjustment({
      unitRate,
      subjectUnitAmount: age,
      compUnitAmount: compAge,
    });
  }

  // Age adjustment is only supported in unit_rate mode
  return undefined;
};

export const selectActualAgeAdj = createPriceSelector(
  [
    selectImprovementsEntities,
    (state, id: string) => id,
    selectCompTypes,
    selectSalesComparisonApproach,
    (state) => selectSubjectUnitRateForFeature(state, 'actualAge'),
  ],
  calculateActualAgeAdj,
);

export const selectActualAgeAdjs = createSelector(
  [
    selectImprovementsEntities,
    selectCompSequence,
    selectCompTypes,
    selectSalesComparisonApproach,
    (state) => selectSubjectUnitRateForFeature(state, 'actualAge'),
  ],
  (improvements, compSequence, compTypes, salesComparisonApproach, unitRate) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateActualAgeAdj(
        improvements,
        propID,
        compTypes,
        salesComparisonApproach,
        unitRate,
      );

      if (adj) acc[propID] = adj;
      return acc;
    }, {}),
);
